import { reactive } from 'vue'
import { AxiosError } from 'axios'
import { ErrorResponse } from '@/model/api/error/erroResponse'
import ERROR_CODE, { ErorrLinkingKey, ErrorLinkingData } from '@/model/api/error/errorCodeType'
import * as log from '@/model/util/log/Logger'
import { convertBlob } from '@/model/api/apiblob'

// エラーメッセージデータ
type ErrorMsgData = {
  // コード
  code: string
  // リソースキー
  key: string
  // 付加情報
  arg: string[]
}

// エラーメッセージ状態
type ErrorMsgState = {
  // データ配列
  data: Array<ErrorMsgData>
}

/**
 * 初期状態を取得する
 * @param error 初期設定するエラー情報
 * @returns reactiveした初期状態
 */
const useErrorMsg = (error?: ErrorMsgState): ErrorMsgState => {
  // 初期データを設定する場合は追加、それ以外は空状態
  const data: Array<ErrorMsgData> = []
  if (error !== undefined) {
    error.data.forEach((p) => data.push(p))
  }
  return reactive<ErrorMsgState>({
    data,
  })
}

/**
 * エラーメッセージが1つの初期状態を取得する
 * @param key リソースキー
 * @returns reactiveした初期状態
 */
const useErrorMsgString = (key: string): ErrorMsgState => {
  // 初期データを設定したデータを返す
  const data: Array<ErrorMsgData> = [
    {
      code: '',
      key,
      arg: [],
    },
  ]
  return reactive<ErrorMsgState>({
    data,
  })
}

/**
 * システムエラーのデータに変換する
 * @param state reactiveしたState
 */
const convertSystemData = (state: ErrorMsgState): void => {
  const list = state.data
  const data: ErrorMsgData = {
    code: '',
    key: ERROR_CODE.SYSTEM.OTHER.key,
    arg: [],
  }
  list.push(data)
}

/**
 * エラーコード紐付け定義からキーデータを設定する
 * @param state reactiveしたState
 * @param input エラーコード紐付け定義
 */
const convertLinkingData = (state: ErrorMsgState, input: ErrorLinkingData): void => {
  const list = state.data
  // 変換したキー一覧情報を取得
  const insertData = input.keys.map((key) => {
    const data: ErrorMsgData = {
      code: '',
      key,
      arg: [],
    }
    return data
  })
  // キー一覧情報をStateに設定する
  insertData.forEach((p) => {
    const data: ErrorMsgData = {
      code: p.code,
      key: p.key,
      arg: p.arg,
    }
    list.push(data)
  })
}

/**
 * APIレスポンスエラーデータをエラーメッセージのデータに変換する
 * @param e APIレスポンス
 * @param state reactiveしたState
 * @param isLinking キー変換するかどうか
 */
const convertData = async (e: AxiosError<ErrorResponse>, state: ErrorMsgState, isLinking?: boolean) => {
  const list = state.data
  // データ削除
  list.splice(0)
  let isTimeout = false
  // タイムアウト or ネットワークエラーの場合はログ出力
  if (e.code === 'ECONNABORTED') {
    // タイムアウト
    isTimeout = true
    log.warn(`api send timeout.`)
  } else if (!e.response) {
    // ネットワークエラー
    log.warn(`api send network error.`)
  }
  // blobを考慮してデータをjsonデータに変換
  const responsedata = await convertBlob(e)
  log.debug(`error responsedata:${JSON.stringify(responsedata)}`)
  // APIのエラーデータ取得できない場合はシステムエラーを設定
  if (isTimeout || !e.response || responsedata === undefined || responsedata.errors === undefined) {
    convertSystemData(state)
  } else {
    // 応答レスポンスが正常な場合はエラーデータを設定する
    const errordata: ErrorResponse = responsedata
    errordata.errors.forEach((p) => {
      const errorLinkingData = ErorrLinkingKey.find((p2) => p2.key === p.resourcekey)
      if (isLinking && errorLinkingData !== undefined) {
        // キー変換して定義一覧にある場合は変換したデータを設定する
        convertLinkingData(state, errorLinkingData)
      } else {
        const data: ErrorMsgData = {
          code: p.code,
          key: p.resourcekey,
          arg: p.additionalinfo,
        }
        list.push(data)
      }
    })
  }
}

/**
 * エラー情報をコピーする
 * @param from コピー元データ
 * @param to コピー先データ
 */
const copyData = (from: ErrorMsgState, to: ErrorMsgState): void => {
  from.data.forEach((p) => to.data.push(p))
}

export {
  ErrorMsgState, //
  ErrorMsgData, //
  useErrorMsg, //
  useErrorMsgString, //
  convertData, //
  convertSystemData, //
  convertLinkingData,
  copyData, //
}
